import './AiCitations.scss';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { renderToStaticMarkup } from 'react-dom/server';

import { useAccount } from '../../../../../shared/contexts/account/AccountContexts';
import { useFilters } from '../../../../../shared/contexts/filters/FiltersContext';
import { ChatType, getChatTypeToName, projectChatType } from '../../../../../shared/types/monitor/ChatType';
import {
  expandSelectedChatTypes,
  getMonitorTypesForChatType,
} from '../../../../../shared/helpers/MonitorTypeHelpers';
import { calculatePercentageShare, getAllSourcesCount } from '../../../../../shared/helpers/StatsHelpers';
import { filterSourcesByDomain } from '../../../../prompt/preview/components/stats/helpers/StatsHelper';
import CustomBarChart from '../../../../../shared/components/chart/custom-bar-chart/CustomBarChart';
import {
  getVerticalBarChartSize,
  prepareAndGetHorizontalBarOptions,
} from '../../../../../shared/components/chart/custom-bar-chart/helpers/CustomBarChartHelper';
import { useLegendState } from '../../../../../shared/components/chart/custom-bar-chart/hooks/UseLegendState';
import { useChartColorPalette } from '../../../../../shared/components/chart/custom-bar-chart/hooks/UseChartColorPalette';
import ChartTooltip from '../../../../../shared/components/chart/custom-bar-chart/components/chart-tooltip/ChartTooltip';
import { LinksData } from '../../../../prompt/preview/components/stats/models/StatsModel';
import CitationsBox from '../CitationsBox';

interface AiCitationsProps {
  sources: LinksData[];
  isAllSelected: boolean;
}

const AiCitations: React.FC<AiCitationsProps> = ({ sources, isAllSelected }) => {
  const { t } = useTranslation();
  const { accountResponse } = useAccount();
  const { domain, selectedChatTypes } = useFilters();
  const [searchParams] = useSearchParams();

  const hasExplicitModelFilter = searchParams.get('models') !== null;

  const allowedMonitorTypes = useMemo(() => {
    if (!hasExplicitModelFilter) return null;
    if (!selectedChatTypes || selectedChatTypes.length === 0) return new Set<string>();
    const expanded = expandSelectedChatTypes(selectedChatTypes);
    const types = new Set<string>();
    for (const ct of Array.from(expanded)) {
      for (const mt of getMonitorTypesForChatType(ct as ChatType)) {
        types.add(mt);
      }
    }
    return types;
  }, [hasExplicitModelFilter, selectedChatTypes]);

  const modelFilteredSources = useMemo(() => {
    if (!allowedMonitorTypes) return sources;
    return sources.filter((s) => s.models.some((m) => allowedMonitorTypes.has(m)));
  }, [sources, allowedMonitorTypes]);

  const filteredSources = useMemo(
    () => filterSourcesByDomain(domain, modelFilteredSources),
    [modelFilteredSources, domain]
  );

  const allSourcesCount = useMemo(
    () => getAllSourcesCount(modelFilteredSources),
    [modelFilteredSources]
  );

  const visibleChatTypes = useMemo(() => {
    const all = projectChatType(accountResponse, true, true);
    if (!allowedMonitorTypes) return all;
    return all.filter((ct) =>
      getMonitorTypesForChatType(ct as ChatType).some((mt) => allowedMonitorTypes.has(mt))
    );
  }, [accountResponse, allowedMonitorTypes]);

  const sourcesDataString: string[][] = useMemo(
    () => [
      ['Label', 'Value'],
      ...visibleChatTypes.map((ct) => {
        const sourcesCount = filteredSources
          .filter((linkData) =>
            linkData.models.some((model) =>
              getMonitorTypesForChatType(ct as ChatType).includes(model)
            )
          )
          .map((linkData) => linkData.count / linkData.models.length);

        const totalForType = sourcesCount.reduce((sum, value) => sum + value, 0);
        return [
          getChatTypeToName(ct as ChatType),
          calculatePercentageShare(totalForType, allSourcesCount, false),
        ];
      }),
    ],
    [visibleChatTypes, filteredSources, allSourcesCount]
  );

  const legendData = useChartColorPalette(sourcesDataString, true);

  const colorMap = useMemo(
    () => new Map(legendData.map((item) => [item.label, item.color])),
    [legendData]
  );

  const tooltipFormatter = (params: any): string =>
    renderToStaticMarkup(<ChartTooltip tooltipFormatterParams={params} />);

  const initialLabels = useMemo(
    () => sourcesDataString.slice(1).map((item) => item[0]),
    [sourcesDataString]
  );

  const { legendStateMap, handleLegendClick } = useLegendState(initialLabels);

  const filteredChartData = useMemo(
    () =>
      sourcesDataString.filter((row, index) => index === 0 || legendStateMap.get(row[0]) !== false),
    [sourcesDataString, legendStateMap]
  );

  const chartOptions = useMemo(
    () => prepareAndGetHorizontalBarOptions(tooltipFormatter, filteredChartData, colorMap),
    [filteredChartData, colorMap]
  );

  if (!isAllSelected) {
    return null;
  }

  return (
    <div className="ai-citations page-box__column">
      <div className="stats__panel">
        <div className="page-box__title-action">
          <div className="page-box__title page-box__title--flex">{t('aiCitations')}</div>
        </div>
        <div className="stats-box stats-box--citations">
          {visibleChatTypes.map((ct: string, index) => (
            <CitationsBox
              chatType={ct as ChatType}
              linksData={filteredSources}
              key={`ai-citations-box-${index}`}
            />
          ))}
        </div>
      </div>
      {sourcesDataString.length > 1 && (
        <div className="stats__panel">
          <div className="stats-box stats-box--pie-chart stats-box--pie-chart-sm">
            <div className="stats-box__content">
              <CustomBarChart
                customEChartsOption={{ eChartsOption: chartOptions, legendData }}
                legendStateMap={legendStateMap}
                onLegendClick={handleLegendClick}
                chartStyle={getVerticalBarChartSize}
                type={'HORIZONTAL'}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiCitations;
